import { autoinject, BindingEngine, customElement, newInstance } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ValidationController, ValidationRules } from 'aurelia-validation';
import { watch } from 'aurelia-watch-decorator';
import { DialogHelper } from 'framework/aurelia_dialogs/dialog-helper';
import { BootstrapGroupRenderer } from 'framework/aurelia_tools/bootstrap-group-renderer';
import { cloneDeep } from 'framework/script_utilities/object-equalizer';
import { MapZoneDetailModel } from 'model/map-zones';
import { MapZonesController } from 'screens/administration/locations/map-zones-controller';
import { ModelBase } from 'support/base_classes/model-base';
import { ToastFormat, ToastHelper, ToastType } from 'support/toaster';
import { ValidationHelper } from 'support/validation';
import { IMapZoneDetailDTO } from 'api/swagger/ApiClient';

@autoinject
@customElement('map-zone-panel')
export class MapZonePanelCustomElement extends ModelBase<IMapZoneDetailDTO> {
  @bindable mapZoneDetailModel: MapZoneDetailModel;
  @bindable mapZonesController: MapZonesController;
  backup: IMapZoneDetailDTO = null;
  submitButtonVisible = false;
  cancelButtonVisible = false;

  constructor(bindingEngine: BindingEngine, private dialogHelper: DialogHelper, private toastHelper: ToastHelper, private validationHelper: ValidationHelper, @newInstance() private validationController: ValidationController, validationRenderer: BootstrapGroupRenderer) {
    super(bindingEngine);
    validationRenderer.allowedParents = 'bs-form-input';
    this.validationController.addRenderer(validationRenderer);
  }

  bind() {
    this.mapZoneDetailModel.requestCancelChanges = () => this.cancel();
    super.bind();
    this.backup = cloneDeep(this.model);
  }

  modelChanged(newValue: IMapZoneDetailDTO) {
    super.modelChanged(newValue);
    ValidationRules
      .ensureObject<IMapZoneDetailDTO>().required()
      .ensure(model => model.name).displayName('Name').required()
      .on(this.model);
    this.validationController.reset();
    this.updateButons();
  }

  async submit() {
    await this.validationHelper.validate(this.validationController) && await this.save();
  }

  async cancel(): Promise<boolean> {
    if (!this.changed || await this.dialogHelper.confirm({ dialogTitle: 'Cancel your changes?', message: 'You haven\'t saved the form yet. If you proceed, your changes will be lost!', okTitle: 'Sure, do it!', cancelTitle: 'Back to form' })) {
      if (this.mapZoneDetailModel.data.id == null)
        this.mapZonesController.cancelNewMapZone();
      else if (this.changed) {
        this.mapZoneDetailModel.data = this.backup;
        this.rebindData(this.backup);
        this.mapZonesController.cancelMapZoneChanges(this.mapZoneDetailModel);
      }
      return true;
    }
    return false;
  }

  private rebindData(data: IMapZoneDetailDTO) {
    this.model = data;
    this.backup = cloneDeep(this.model);
  }

  private async save() {
    await this.mapZonesController.saveMapZone(this.mapZoneDetailModel);
    this.rebindData(this.mapZoneDetailModel.data);
    this.toastHelper.toast('Zone has been successfully <b>saved</b>.', 'Saved', ToastType.Success, ToastFormat.Html);
  }

  @watch((x: MapZonePanelCustomElement) => x.changed)
  private updateModel() {
    this.updateButons();
    this.mapZoneDetailModel.isEdited = this.changed;
  }

  private updateButons() {
    this.submitButtonVisible = this.changed || !this.mapZoneDetailModel.data.id;
    this.cancelButtonVisible = this.changed || !this.mapZoneDetailModel.data.id;
  }
}
